import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import { articlesAPI } from '../services/api';
import { Calendar, User, ExternalLink, Share2, Facebook, Twitter, Mail, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import Comments from '../components/Comments';

const ArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    window.scrollTo(0, 0);
    loadArticle();
  }, [slug]);

  const loadArticle = async () => {
    try {
      const data = await articlesAPI.getBySlug(slug);
      setArticle(data);
    } catch (error) {
      console.error('Error loading article:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <Loader2 className="animate-spin mx-auto mb-4" size={48} />
        <p className="text-gray-600">Loading article...</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h1 className="text-3xl font-bold mb-4">Article Not Found</h1>
        <Link to="/" className="text-blue-600 hover:underline">
          Return to Homepage
        </Link>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const text = article.title;
    
    let shareUrl = '';
    
    switch(platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(url)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        toast({
          title: "Link Copied!",
          description: "Article link copied to clipboard"
        });
        return;
      default:
        return;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  
  // Determine badge type based on category
  const isCommunityStory = ['youth-sports', 'local-heroes', 'community-events', 'good-deeds'].includes(article.category);
  const badgeText = isCommunityStory ? 'COMMUNITY STORY' : 'COUNTER STORY';
  
  // Generate custom OG image URL
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://cctimes.au';
  const ogImageUrl = `${backendUrl}/api/og-image/?title=${encodeURIComponent(article.title)}&category=${encodeURIComponent(article.category)}&badge=${encodeURIComponent(badgeText)}`;
  
  // Generate JSON-LD structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.image || ogImageUrl,
    "datePublished": article.published_date,
    "dateModified": article.published_date,
    "author": {
      "@type": "Person",
      "name": article.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "The Central Coast Times",
      "logo": {
        "@type": "ImageObject",
        "url": "https://cctimes.au/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": currentUrl
    },
    "articleSection": article.category,
    "keywords": article.category.replace('-', ' ')
  };

  return (
    <>
      <Helmet>
        <title>{article.title} | The Central Coast Times</title>
        <meta name="description" content={article.excerpt} />
        <meta name="keywords" content={`Central Coast news, ${article.category}, ${article.title}, Central Coast Times`} />
        <link rel="canonical" href={currentUrl} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="The Central Coast Times" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.excerpt} />
        <meta name="twitter:image" content={ogImageUrl} />
        
        {/* Article specific meta */}
        <meta property="article:published_time" content={article.published_date} />
        <meta property="article:author" content={article.author} />
        <meta property="article:section" content={article.category} />
        
        {/* JSON-LD Structured Data for Google */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      <article className="max-w-4xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-600 mb-6">
        <Link to="/" className="hover:text-gray-900">Home</Link>
        <span className="mx-2">/</span>
        <Link to={`/category/${article.category}`} className="hover:text-gray-900 capitalize">
          {article.category.replace('-', ' ')}
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-900">{article.title}</span>
      </nav>

      {/* Category Badge - Subtle category only */}
      <div className="mb-4">
        <span className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
          {article.category.replace('-', ' ')}
        </span>
      </div>

      {/* Title */}
      <h1 className="text-4xl lg:text-5xl font-serif font-bold leading-tight mb-6">
        {article.title}
      </h1>

      {/* Meta Info */}
      <div className="flex flex-wrap items-center gap-4 text-gray-600 mb-6 pb-6 border-b border-gray-200">
        <Link 
          to={`/author/${encodeURIComponent(article.author)}`}
          className="flex items-center gap-2 hover:text-blue-600 transition-colors"
        >
          <User size={18} />
          <span className="font-medium">{article.author}</span>
        </Link>
        <div className="flex items-center gap-2">
          <Calendar size={18} />
          <span>{formatDate(article.published_date)}</span>
        </div>
      </div>

      {/* Original Article Reference - Removed as per client request */}

      {/* Featured Image - Only show if not already in content */}
      {article.image && !article.content?.includes(article.image) && (
        <div className="mb-8">
          <img
            src={article.image.startsWith('http') ? article.image : `${backendUrl}${article.image}`}
            alt={article.title}
            className="w-full h-auto rounded-sm"
            onError={(e) => {
              // Hide image if it fails to load
              e.target.style.display = 'none';
            }}
          />
          {article.image && (
            <p className="text-sm text-gray-600 mt-2 italic">
              {article.title}
            </p>
          )}
        </div>
      )}

      {/* Photos are now embedded within article content - no separate gallery needed */}

      {/* Article Content */}
      <div 
        className="prose prose-lg max-w-none mb-8 article-content"
        style={{
          fontSize: '1.125rem',
          lineHeight: '1.8',
          color: '#1f2937'
        }}
      >
        {article.content && article.content.includes('<p>') ? (
          // If content contains HTML tags, render as HTML
          <div dangerouslySetInnerHTML={{ __html: article.content }} />
        ) : (
          // If content is Markdown, use ReactMarkdown
          <ReactMarkdown
            components={{
              img: ({node, ...props}) => (
                <img 
                  {...props} 
                  className="w-full h-auto rounded-lg my-6" 
                  alt={props.alt || 'Article image'}
                />
              ),
              h2: ({node, ...props}) => (
                <h2 {...props} className="text-2xl font-bold mt-8 mb-4" />
              ),
              p: ({node, ...props}) => (
                <p {...props} className="mb-4" />
              )
            }}
          >
            {article.content}
          </ReactMarkdown>
        )}
      </div>

      {/* Share Buttons */}
      <div className="border-t border-b border-gray-200 py-6 mb-8">
        <div className="flex items-center gap-4">
          <span className="font-semibold text-gray-900 flex items-center gap-2">
            <Share2 size={18} />
            Share this article:
          </span>
          <button
            onClick={() => handleShare('facebook')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Share on Facebook"
          >
            <Facebook size={20} className="text-blue-600" />
          </button>
          <button
            onClick={() => handleShare('twitter')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Share on Twitter"
          >
            <Twitter size={20} className="text-blue-400" />
          </button>
          <button
            onClick={() => handleShare('email')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Share via Email"
          >
            <Mail size={20} className="text-gray-600" />
          </button>
          <button
            onClick={() => handleShare('copy')}
            className="px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800 transition-colors font-medium"
          >
            Copy Link
          </button>
        </div>
      </div>

      {/* About Section */}
      <div className="bg-gray-50 rounded-lg p-6 mb-8">
        <h3 className="font-bold text-xl mb-3">About The Central Coast Times</h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          We believe every story deserves to be told from all perspectives. When the Central Coast Express Advocate publishes one-sided stories that damage reputations, we provide a platform for the other side to be heard.
        </p>
        <Link
          to="/submit-story"
          className="inline-block px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-semibold"
        >
          Share Your Story
        </Link>
      </div>

      {/* Comments Section */}
      <Comments articleId={article._id || article.id} />
    </article>
    </>
  );
};

export default ArticlePage;